import {Button, Colors, Menu, MenuItem} from "@blueprintjs/core";
import {
    IItemListRendererProps,
    IItemRendererProps,
    ItemListPredicate,
    ItemListRenderer,
    ItemRenderer,
    Select,
} from "@blueprintjs/select";
import {ClassNames} from "@emotion/react";
import styled from "@emotion/styled";
import * as React from "react";

import {TagItem} from "../tag-cache/tag-cache";

type LooseTagItem = TagItem | null;

function createKey(item: LooseTagItem) {
    return item ? item.slug : "";
}

const TagSelect = Select.ofType<LooseTagItem>();

function createItemListPredicate(emptyOption?: boolean): ItemListPredicate<LooseTagItem> {
    return (query: string, items: LooseTagItem[]) => {
        const arr: LooseTagItem[] = [];

        if (emptyOption && !query) {
            arr.push(null);
        }

        return arr.concat(
            items.filter(
                (item) =>
                    !query ||
                    (item && item.label.indexOf(query) >= 0) ||
                    (item && item.slug.indexOf(query) >= 0),
            ),
        );
    };
}

const TagSelectorListMenu = styled(Menu)`
    max-height: 50vh;
    max-width: 330px;
    overflow: auto;
`;

const itemListRenderer: ItemListRenderer<LooseTagItem> = (
    props: IItemListRendererProps<LooseTagItem>,
) => {
    const elements: React.ReactNode[] = [];

    let index = 0;
    for (const item of props.filteredItems) {
        if (!item) {
            elements.push(props.renderItem(item, index++));
            continue;
        }

        elements.push(props.renderItem(item, index++));
    }

    if (!elements.length) {
        elements.push(renderNonIdeal());
    }

    return <TagSelectorListMenu ulRef={props.itemsParentRef}>{elements}</TagSelectorListMenu>;
};

function createItemRenderer(placeholder?: string): ItemRenderer<LooseTagItem> {
    return (item, props) => (item ? renderItem(item, props) : renderEmptyItem(props, placeholder));
}

function renderEmptyItem(props: IItemRendererProps, placeholder?: string) {
    return (
        <MenuItem
            active={props.modifiers.active}
            key={createKey(null)}
            text={renderItemContent(null, placeholder)}
            onClick={props.handleClick}
        />
    );
}

function renderItem(item: TagItem, props: IItemRendererProps) {
    return (
        <MenuItem
            active={props.modifiers.active}
            key={createKey(item)}
            text={
                <>
                    <span style={{marginLeft: "1em"}} />
                    {renderItemContent(item)}
                </>
            }
            onClick={props.handleClick}
        />
    );
}

function renderNonIdeal() {
    return <MenuItem disabled={true} key={createKey(null)} text={"No results."} />;
}

const TagSpan = styled.span``;

const TagSlugSpan = styled.span`
    margin-left: 0.2em;
    color: ${Colors.GRAY3};
`;

function renderItemContent(item: LooseTagItem, placeholder?: string) {
    return item ? (
        <TagSpan>
            {item.label}
            <TagSlugSpan>({item.slug})</TagSlugSpan>
        </TagSpan>
    ) : (
        <span style={{color: Colors.GRAY3}}>{placeholder || "未選択"}</span>
    );
}

interface ITagSelectorProps {
    value: string | null;
    emptyOption?: boolean;
    placeholder?: string;
    onChange: (item: LooseTagItem) => void;
    isBold?: boolean;
    mediaId: string;
    availableTags: TagItem[];
}

export const TagSelector: React.FC<ITagSelectorProps> = (props) => {
    const handleChange = (item: LooseTagItem) => {
        props.onChange(item);
    };

    const items = props.availableTags;

    const selectedItem = items.find((x) => x.slug === props.value) || null;

    return (
        <ClassNames>
            {({css}) => (
                <TagSelectWithChildren
                    items={items}
                    itemListPredicate={createItemListPredicate(props.emptyOption)}
                    itemRenderer={createItemRenderer(props.placeholder)}
                    itemListRenderer={itemListRenderer}
                    onItemSelect={handleChange}
                    popoverProps={{
                        modifiers: {arrow: {enabled: false}},
                        popoverClassName: css`
                            padding: 5px;
                            background-color: white;
                        `,
                    }}
                    resetOnClose={true}
                    resetOnSelect={true}
                    resetOnQuery={false}
                >
                    <Button rightIcon="caret-down">
                        {renderItemContent(selectedItem, props.placeholder)}
                    </Button>
                </TagSelectWithChildren>
            )}
        </ClassNames>
    );
};

interface TagSelectWithChildrenProps extends React.ComponentProps<typeof TagSelect> {
    children: React.ReactNode;
}

const TagSelectWithChildren: React.FC<TagSelectWithChildrenProps> = (props) => {
    return <TagSelect {...props}>{props.children}</TagSelect>;
};
